// searchCorpus — the ONE library→search-corpus projection, lifted out of
// OmnibarHost so every search surface folds the same corpus (no drift).
//
// The search ENGINE (parse + fuzzy score) already had a shared home in
// `components/Search/query.ts`; the CORPUS (the list of searchable library
// resources) did NOT — it was built inline inside `useOmnibarHost`. That made
// the omnibar the only surface that could reach it. The in-card reference
// search (ContextSideCard) needs the SAME resource rows, so this module is the
// shared home: `libraryCommandItems` maps a `Library` into the palette's
// `CommandItem` resource rows, and OmnibarHost appends its non-resource rows
// (the ambient conversation, panel commands, authoring verbs, app/dev commands)
// on top. One projection, two consumers — they cannot drift.
//
// Pure: a plain data→data map, no I/O. `Library` is a read-model DTO (a value,
// not a port), so this stays a pure projection the component tree can call.

import type { CommandItem } from "../components/CommandPalette/CommandPalette.js";
import { resourceDisplayTitle } from "../components/Navigation/displayTitle.js";
import type { Library } from "./ports.js";

// The workspace's flat pool of resources as palette rows, in the rail's order
// (materials / sources / exports — the authoring flow). The per-map return
// annotation keeps each `kind` literal a `CommandKind` without an `as` cast.
// A null library (not yet loaded) yields no rows.
export const libraryCommandItems = (
    library: Library | null,
    untitled: string,
): readonly CommandItem[] =>
    library === null
        ? []
        : [
              ...library.materials.map(
                  (m): CommandItem => ({
                      id: m.id,
                      title: resourceDisplayTitle(
                          m.title,
                          "material",
                          untitled,
                      ),
                      kind: "material",
                  }),
              ),
              ...library.sources.map(
                  (s): CommandItem => ({
                      id: s.id,
                      title: resourceDisplayTitle(s.title, "source", untitled),
                      kind: "source",
                  }),
              ),
              ...library.exports.map(
                  (e): CommandItem => ({
                      id: e.id,
                      title: resourceDisplayTitle(e.title, "export", untitled),
                      kind: "export",
                  }),
              ),
          ];
